import React, { Suspense } from "react";
import { Navigate } from "react-router-dom";
import ProtectedRoute from "ProtectedRoute";
import DashboardIcon from "@mui/icons-material/Dashboard";

// LCP route table. Sidebar nav layout lives in lcp/components/LcpPortalNavigation.js
// — these entries only feed the router (App.js builds <Route>s from them)
// and the legacy main Sidenav's appMode filter. Every entry is tagged
// `appMode: "lcp"` so it never leaks into other shells.
//
// Wave 1 (Stage 0/1): Overview + lift detail + calibration + alerts.
// Wave 2 (this batch): full nav buildout — Tickets, Payables, Inspection
// Command Center, Client Assets, Receivables, plus Settings sub-pages
// that re-mount the AFMS Settings components inside the LCP shell.
const LiftDetail = React.lazy(() => import("lcp/pages/LiftDetail"));
const Calibration = React.lazy(() => import("lcp/pages/Calibration"));
const Alerts = React.lazy(() => import("lcp/pages/Alerts"));

// Wave 2 domain pages
const LcpLiftControlCenter = React.lazy(() => import("lcp/pages/LiftControlCenter"));
const LcpTickets = React.lazy(() => import("lcp/pages/Tickets"));
const LcpPayables = React.lazy(() => import("lcp/pages/Payables"));
// Approvals inbox — gate-driven engine inbox (caller-pending decisions).
// The older InspectionApprovalsInbox is kept on disk but no longer
// routed; the engine path is the single approvals surface.
// Personal cross-system inbox. The same page the environmental portal
// mounts at /afms/inbox — it renders bare (no shell of its own) and reads
// notifications-api /api/notifications/inbox, whose recipient comes from the
// bearer token, so it is portal-agnostic and needs no LCP-specific copy.
const LcpInbox = React.lazy(() => import("afms/pages/Inbox"));
const LcpApprovalsInbox = React.lazy(() =>
  import("lcp/pages/Approvals/EngineInbox")
);
// Sign-off Dashboard — every submitted inspection per dealership, how long it
// has waited and on whom, a Nudge button and a printable record (Jam 8e1c8840).
const LcpSignoffDashboard = React.lazy(() => import("lcp/pages/Approvals/SignoffDashboard"));
const LcpInspectionCommandCenter = React.lazy(() => import("lcp/pages/InspectionCommandCenter"));
const LcpClientAssets = React.lazy(() => import("lcp/pages/ClientAssets"));
const LcpOrderLabels = React.lazy(() => import("lcp/pages/OrderLabels"));
const LcpReceivables = React.lazy(() => import("lcp/pages/Receivables"));
const LcpSchedule = React.lazy(() => import("lcp/pages/Schedule"));
const LcpInspectionResultsReport = React.lazy(() =>
  import("lcp/pages/InspectionResultsReport")
);
const LcpMyLifts = React.lazy(() => import("lcp/pages/MyLifts"));

// Wave 2 — Inspection Command Center children mounted from backoffice pages
const LcpInspections = React.lazy(() => import("lcp/pages/Inspections"));
const LcpInspectionForms = React.lazy(() => import("lcp/pages/InspectionForms"));
const LcpNewInspectionForm = React.lazy(() => import("lcp/pages/NewInspectionForm"));
const LcpNewDailyInspectionForm = React.lazy(() => import("lcp/pages/NewDailyInspectionForm"));

// LCP Inspection PWA — mobile shell + lift picker + daily form. Meant
// to run at /lcpinspectionapp on any subdomain so an operator can
// install it to their phone's home screen and file dailies in the field.
const LcpInspectionAppHome = React.lazy(() =>
  import("lcp/pages/InspectionApp")
);
const LcpInspectionAppLiftPicker = React.lazy(() =>
  import("lcp/pages/InspectionApp/LiftPicker")
);
const LcpInspectionAppMobileDaily = React.lazy(() =>
  import("lcp/pages/InspectionApp/MobileDaily")
);
const LcpInspectionAppTrainingHome = React.lazy(() =>
  import("lcp/pages/InspectionApp/TrainingHome")
);
const LcpInspectionAppTrainingEntryForm = React.lazy(() =>
  import("lcp/pages/InspectionApp/TrainingEntryForm")
);
const LcpOperatorTrainingLog = React.lazy(() => import("lcp/pages/OperatorTrainingLog"));

// Vendor-initiated yearly inspection (from /lcp/client-assets).
const LcpNewInspection = React.lazy(() => import("lcp/pages/NewInspection"));
const LcpEditInspection = React.lazy(() => import("lcp/pages/EditInspection"));

// Daily Operator Lift Inspection — shorter pre-shift checklist run by
// vendors (or in-house operators) from /lcp/client-assets.
const LcpNewDailyInspection = React.lazy(() => import("lcp/pages/NewDailyInspection"));
const LcpDailyInspectionForm = React.lazy(() => import("lcp/pages/DailyInspectionForm"));

// Per-lift Operator Training Record (ANSI/ALI ALOIM:2008(R2013) §4.x).
// Launched from the Inspections "Operator Training" tab. v1 is layout
// only — backend persistence wires up after data-model approval.
const LcpOperatorTrainingForm = React.lazy(() => import("lcp/pages/OperatorTrainingForm"));

// Wave 2 settings sub-pages (thin wrappers around AFMS Settings pages)
const LcpSettingsOrganization = React.lazy(() => import("lcp/pages/Settings/Organization"));
const LcpSettingsUsers = React.lazy(() => import("lcp/pages/Settings/Users"));
const LcpSettingsAssets = React.lazy(() => import("lcp/pages/Settings/Assets"));
// Lift Onboarding Wizard: walk a rooftop bay by bay against the location tree
// (spec LC-SPC-OW-01). Admin only; gated in the nav and on the page itself.
const LcpLiftOnboarding = React.lazy(() => import("lcp/pages/LiftOnboarding"));
const LcpSettingsDevices = React.lazy(() => import("lcp/pages/Settings/Devices"));
const LcpSettingsTickets = React.lazy(() => import("lcp/pages/Settings/Tickets"));
const LcpApprovalRules = React.lazy(() => import("lcp/pages/Settings/ApprovalRules"));
const LcpMyLiftInspections = React.lazy(() => import("lcp/pages/MyLiftInspections"));
const LcpLiftView = React.lazy(() => import("lcp/pages/LiftView"));
const LcpApprovalGates = React.lazy(() => import("lcp/pages/Settings/ApprovalGates"));
const LcpApprovalNotifications = React.lazy(() => import("lcp/pages/Settings/ApprovalNotifications"));
const UploadLogoPage = React.lazy(() =>
  import("lcp/pages/Settings/LcpSettings/UploadLogoPage")
);
const VerbagePage = React.lazy(() =>
  import("lcp/pages/Settings/LcpSettings/VerbagePage")
);
const ResultsActionsPage = React.lazy(() =>
  import("lcp/pages/Settings/LcpSettings/ResultsActionsPage")
);
const GeneralSettingsPage = React.lazy(() =>
  import("lcp/pages/Settings/LcpSettings/GeneralSettingsPage")
);

// Resources Setup — org-owned inspection reference catalogs (make/models,
// task / comment / deferral templates). Each org maintains its own; the
// inspection form reads the acting org's rows (RLS-scoped in lcp-api).
const ResourcesLiftModelsPage = React.lazy(() =>
  import("lcp/pages/ResourcesSetup/LiftModelsPage")
);
const ResourcesTasksPage = React.lazy(() =>
  import("lcp/pages/ResourcesSetup/TasksPage")
);
const ResourcesCommentsPage = React.lazy(() =>
  import("lcp/pages/ResourcesSetup/CommentsPage")
);
const ResourcesDeferralsPage = React.lazy(() =>
  import("lcp/pages/ResourcesSetup/DeferralsPage")
);

// Enterprise (Manage my Clients / Manage my Vendors) — same shared pages
// the AFMS + Energy portals mount. Sidebar gating (requiresEnterpriseOwner)
// keeps these hidden unless the caller is the OWNER of an enterprise org.
const EnterpriseManageMyClients = React.lazy(() => import("pages/Enterprise/ManageMyClients"));
const EnterpriseManageMyVendors = React.lazy(() => import("pages/Enterprise/ManageMyVendors"));

// Shorthand for the standard wrap so the route list stays scannable.
// ProtectedRoute renders ONLY its `component` prop (see src/ProtectedRoute.js:54
// `return Component;` — children are discarded). The previous version of this
// helper passed the Suspense tree as children → every LCP route rendered blank.
// Mirroring the prop pattern routes.js uses everywhere else fixes all of them.
const wrap = (Component) => (
  <ProtectedRoute
    component={
      <Suspense fallback={null}>
        <Component />
      </Suspense>
    }
  />
);

const lcpRoutes = [
  {
    type: "collapse",
    name: "Main",
    key: "lcp-main",
    icon: <DashboardIcon />,
    route: "/lcp/overview",
    appMode: "lcp",
    // Was the Stage-0 API-health smoke page, and the LCP login landing — so
    // every customer's first screen was raw health JSON. Old pinned homes
    // and bookmarks still point here; send them to the real landing page.
    component: <Navigate to="/lcp/lift-control-center" replace />,
    noCollapse: true,
  },
  // Hidden detail routes — reachable from inside other pages, not from the nav.
  {
    type: "hidden",
    key: "lcp-lift-detail",
    route: "/lcp/lifts/:liftId",
    appMode: "lcp",
    component: wrap(LiftDetail),
  },
  {
    type: "hidden",
    key: "lcp-lift-calibration",
    route: "/lcp/lifts/:liftId/calibration",
    appMode: "lcp",
    component: wrap(Calibration),
  },
  {
    type: "collapse",
    name: "Alerts",
    key: "lcp-alerts",
    icon: <DashboardIcon />,
    route: "/lcp/alerts",
    appMode: "lcp",
    component: wrap(Alerts),
    noCollapse: true,
  },

  // Wave 2 — Lift Control Center landing page
  {
    type: "hidden",
    key: "lcp-lift-control-center",
    route: "/lcp/lift-control-center",
    appMode: "lcp",
    component: wrap(LcpLiftControlCenter),
  },

  // Wave 2 — Lift Control Center children
  {
    type: "hidden",
    key: "lcp-tickets",
    route: "/lcp/tickets",
    appMode: "lcp",
    component: wrap(LcpTickets),
  },
  {
    type: "hidden",
    key: "lcp-payables",
    route: "/lcp/payables",
    appMode: "lcp",
    component: wrap(LcpPayables),
  },
  {
    type: "hidden",
    key: "lcp-approvals-inbox",
    route: "/lcp/approvals/inbox",
    appMode: "lcp",
    component: wrap(LcpApprovalsInbox),
  },
  {
    type: "hidden",
    key: "lcp-signoff-dashboard",
    route: "/lcp/approvals/signoff-dashboard",
    appMode: "lcp",
    component: wrap(LcpSignoffDashboard),
  },
  {
    type: "hidden",
    key: "lcp-inbox",
    route: "/lcp/inbox",
    appMode: "lcp",
    component: wrap(LcpInbox),
  },

  // Wave 2 — Inspection Command Center + children
  {
    type: "hidden",
    key: "lcp-inspection-command-center",
    route: "/lcp/inspection-command-center",
    appMode: "lcp",
    component: wrap(LcpInspectionCommandCenter),
  },
  {
    type: "hidden",
    key: "lcp-client-assets",
    route: "/lcp/client-assets",
    appMode: "lcp",
    component: wrap(LcpClientAssets),
  },
  {
    type: "hidden",
    key: "lcp-order-labels",
    route: "/lcp/order-labels",
    appMode: "lcp",
    component: wrap(LcpOrderLabels),
  },
  {
    type: "hidden",
    key: "lcp-receivables",
    route: "/lcp/receivables",
    appMode: "lcp",
    component: wrap(LcpReceivables),
  },
  {
    type: "hidden",
    key: "lcp-schedule",
    route: "/lcp/schedule",
    appMode: "lcp",
    component: wrap(LcpSchedule),
  },
  {
    // Inspection Results Report — what the visit found, across a scope of
    // lifts. Deliberately its own top-level path: nesting it under
    // /lcp/inspections/... would light up the Inspections nav item, because
    // LcpPortalSidebar matches on pathname.startsWith(route + "/").
    type: "hidden",
    key: "lcp-inspection-results",
    route: "/lcp/inspection-results",
    appMode: "lcp",
    component: wrap(LcpInspectionResultsReport),
  },
  {
    // My Lifts — the operator's view, and the QR target on the machine:
    // /lcp/my-lifts?lift=<assetPublicId>. That parameter names a LIFT; the
    // operator is resolved from the session server-side, never from the URL
    // (spec §9.6).
    type: "hidden",
    key: "lcp-my-lifts",
    route: "/lcp/my-lifts",
    appMode: "lcp",
    component: wrap(LcpMyLifts),
  },
  {
    type: "hidden",
    key: "lcp-inspections",
    route: "/lcp/inspections",
    appMode: "lcp",
    component: wrap(LcpInspections),
  },
  {
    type: "hidden",
    key: "lcp-inspection-forms",
    route: "/lcp/inspection-forms",
    appMode: "lcp",
    component: wrap(LcpInspectionForms),
  },
  {
    // View-only preview of the upcoming lift-type-aware inspection
    // form. Lives under the LCP "Inspections" nav group.
    type: "hidden",
    key: "lcp-new-inspection-form",
    route: "/lcp/new-inspection-form",
    appMode: "lcp",
    component: wrap(LcpNewInspectionForm),
  },
  {
    // "Start a new inspection" trigger (Client Assets, Lift Control
    // Center) lands here with ?assetPublicId=… query string. New form
    // creates the draft inspection on first edit, prefilled with the
    // asset's identity fields server-side.
    type: "hidden",
    key: "lcp-inspections-new",
    route: "/lcp/inspections/new",
    appMode: "lcp",
    component: wrap(LcpNewInspectionForm),
  },
  {
    // Pre-shift Operator Daily Lift Inspection — frontend-only stub
    // for now, no backend persistence.
    type: "hidden",
    key: "lcp-new-daily-inspection-form",
    route: "/lcp/new-daily-inspection-form",
    appMode: "lcp",
    component: wrap(LcpNewDailyInspectionForm),
  },
  {
    // ANSI/ALI ALOIM:2020 Appendix A Operator Training Log —
    // per-operator training record. Frontend-only stub.
    type: "hidden",
    key: "lcp-operator-training-log",
    route: "/lcp/operator-training-log",
    appMode: "lcp",
    component: wrap(LcpOperatorTrainingLog),
  },
  {
    // Resume / edit an existing inspection — same form, hydrated from
    // /yearly-inspections/{publicId} on mount.
    type: "hidden",
    key: "lcp-inspections-edit",
    route: "/lcp/inspections/:publicId",
    appMode: "lcp",
    component: wrap(LcpNewInspectionForm),
  },
  {
    type: "hidden",
    key: "lcp-daily-inspections-new",
    route: "/lcp/daily-inspections/new",
    appMode: "lcp",
    component: wrap(LcpNewDailyInspection),
  },

  // LCP Inspection PWA — mobile shell under /lcpinspectionapp. All
  // three routes are ProtectedRoute-wrapped; unauth visitors get
  // redirected to /login and land back here via postLoginRedirect
  // (see ProtectedRoute.js + pages/Login/index.js).
  {
    type: "hidden",
    key: "lcp-inspection-app-home",
    route: "/lcpinspectionapp",
    appMode: "lcp",
    component: wrap(LcpInspectionAppHome),
  },
  {
    type: "hidden",
    key: "lcp-inspection-app-daily-picker",
    route: "/lcpinspectionapp/daily",
    appMode: "lcp",
    component: wrap(LcpInspectionAppLiftPicker),
  },
  {
    type: "hidden",
    key: "lcp-inspection-app-daily-form",
    route: "/lcpinspectionapp/daily/:assetPublicId",
    appMode: "lcp",
    component: wrap(LcpInspectionAppMobileDaily),
  },
  {
    type: "hidden",
    key: "lcp-inspection-app-training-home",
    route: "/lcpinspectionapp/training",
    appMode: "lcp",
    component: wrap(LcpInspectionAppTrainingHome),
  },
  {
    type: "hidden",
    key: "lcp-inspection-app-training-new",
    route: "/lcpinspectionapp/training/new",
    appMode: "lcp",
    component: wrap(LcpInspectionAppTrainingEntryForm),
  },
  {
    type: "hidden",
    key: "lcp-daily-inspections-form",
    route: "/lcp/daily-inspections/:publicId",
    appMode: "lcp",
    // Point at the new form so "In progress" cards on /lcp/lift-control-center
    // and the edit pencil on /lcp/inspections both open the SAME UI as a
    // fresh "Not done" click. The component reads :publicId via useParams
    // and hydrates from the existing row. Legacy DailyInspectionForm kept
    // as a code-split chunk for now in case we need to revert.
    component: wrap(LcpNewDailyInspectionForm),
  },
  {
    type: "hidden",
    key: "lcp-operator-training-new",
    route: "/lcp/operator-training/new",
    appMode: "lcp",
    component: wrap(LcpOperatorTrainingForm),
  },

  // Lift View — one lift at a time: scoped location picker, lift picker,
  // the lift's own data, its inspection history behind an icon, and every
  // corrective action ever raised against it.
  {
    type: "hidden",
    key: "lcp-lift-view",
    route: "/lcp/lift-view",
    appMode: "lcp",
    component: wrap(LcpLiftView),
  },

  // My Lift Inspections — read-only browse + PDF download for every
  // inspection at a location the caller picks. Sits next to the
  // Approvals inbox in the sidebar.
  {
    type: "hidden",
    key: "lcp-my-lift-inspections",
    route: "/lcp/approvals/my-lift-inspections",
    appMode: "lcp",
    component: wrap(LcpMyLiftInspections),
  },

  // Approvals — Settings → Approvals subgroup (port-in-progress from AFMS)
  {
    type: "hidden",
    key: "lcp-settings-approval-rules",
    route: "/lcp/settings/approval-rules",
    appMode: "lcp",
    component: wrap(LcpApprovalRules),
  },
  {
    type: "hidden",
    key: "lcp-settings-approval-gates",
    route: "/lcp/settings/approval-gates",
    appMode: "lcp",
    component: wrap(LcpApprovalGates),
  },
  {
    type: "hidden",
    key: "lcp-settings-approval-notifications",
    route: "/lcp/settings/approval-notifications",
    appMode: "lcp",
    component: wrap(LcpApprovalNotifications),
  },

  // Wave 2 — Settings sub-pages
  {
    type: "hidden",
    key: "lcp-settings-organization",
    route: "/lcp/settings/organization",
    appMode: "lcp",
    component: wrap(LcpSettingsOrganization),
  },
  {
    type: "hidden",
    key: "lcp-settings-users",
    route: "/lcp/settings/users",
    appMode: "lcp",
    component: wrap(LcpSettingsUsers),
  },
  {
    type: "hidden",
    key: "lcp-settings-assets",
    route: "/lcp/settings/assets",
    appMode: "lcp",
    component: wrap(LcpSettingsAssets),
  },
  {
    type: "hidden",
    key: "lcp-settings-lift-onboarding",
    route: "/lcp/settings/lift-onboarding",
    appMode: "lcp",
    component: wrap(LcpLiftOnboarding),
  },
  {
    type: "hidden",
    key: "lcp-settings-devices",
    route: "/lcp/settings/devices",
    appMode: "lcp",
    component: wrap(LcpSettingsDevices),
  },
  {
    type: "hidden",
    key: "lcp-settings-tickets",
    route: "/lcp/settings/tickets",
    appMode: "lcp",
    component: wrap(LcpSettingsTickets),
  },
  {
    type: "hidden",
    key: "lcp-settings-general",
    route: "/lcp/settings/general",
    appMode: "lcp",
    component: wrap(GeneralSettingsPage),
  },
  {
    type: "hidden",
    key: "lcp-settings-certificate-setup-upload-logo",
    route: "/lcp/settings/certificate-setup/upload-logo",
    appMode: "lcp",
    component: wrap(UploadLogoPage),
  },
  {
    type: "hidden",
    key: "lcp-settings-certificate-setup-verbage",
    route: "/lcp/settings/certificate-setup/verbage",
    appMode: "lcp",
    component: wrap(VerbagePage),
  },
  {
    type: "hidden",
    key: "lcp-settings-certificate-setup-results-actions",
    route: "/lcp/settings/certificate-setup/results-actions",
    appMode: "lcp",
    component: wrap(ResultsActionsPage),
  },
  // Resources Setup — hidden routes (sidebar nests them under LCP Settings).
  {
    type: "hidden",
    key: "lcp-resources-lift-models",
    route: "/lcp/resources/lift-models",
    appMode: "lcp",
    component: wrap(ResourcesLiftModelsPage),
  },
  {
    type: "hidden",
    key: "lcp-resources-tasks",
    route: "/lcp/resources/tasks",
    appMode: "lcp",
    component: wrap(ResourcesTasksPage),
  },
  {
    type: "hidden",
    key: "lcp-resources-comments",
    route: "/lcp/resources/comments",
    appMode: "lcp",
    component: wrap(ResourcesCommentsPage),
  },
  {
    type: "hidden",
    key: "lcp-resources-deferrals",
    route: "/lcp/resources/deferrals",
    appMode: "lcp",
    component: wrap(ResourcesDeferralsPage),
  },
  // Enterprise — hidden routes (sidebar gates visibility separately).
  {
    type: "hidden",
    key: "lcp-enterprise-manage-clients",
    route: "/lcp/enterprise/manage-clients",
    appMode: "lcp",
    component: wrap(EnterpriseManageMyClients),
  },
  {
    type: "hidden",
    key: "lcp-enterprise-manage-vendors",
    route: "/lcp/enterprise/manage-vendors",
    appMode: "lcp",
    component: wrap(EnterpriseManageMyVendors),
  },
];

export default lcpRoutes;
